import React, { useState } from "react";
import styles from "./App.module.scss";

import { getCadenceLimits } from "@jugglegym/core/lib/pattern/utils";
import { Parser } from "@jugglegym/core/lib/pattern/parser";
import { Controller } from "@jugglegym/core/lib/controller";
import { Canvas2DRenderer } from "@jugglegym/core/lib/renderer/canvas2d";
import { Renderer } from "@jugglegym/core/lib/renderer/base";
import "normalize.css/normalize.css";
import { PlayButton } from "./PlayButton";
import { RangeInput } from "./RangeInput";
import {
  parseHandSequence,
  HandSequence,
  HandSequenceChoices,
  HandSequenceMap,
  DefaultHandSequenceName,
} from "@jugglegym/core/lib/handmovement";
import { Library } from "./Library";
import { Generator } from "./Generator";
import { Pattern } from "./patterndb";
import { isPlayableSiteswap } from "@jugglegym/core/lib/player";

interface EngineProps {
  paused: boolean;
  handSequence: HandSequence;
  siteswap: string;
  timeScale: number;
  dwellTimeF: number;
  beatShiftF: number;
  cycleDuration: number;
}

class Engine extends React.PureComponent<EngineProps, {}> {
  #ctrl: Controller | null = null;
  #canvasRef: React.RefObject<HTMLCanvasElement>;

  constructor(props: EngineProps) {
    super(props);
    this.#canvasRef = React.createRef();
  }

  componentDidMount() {
    this.createController();
  }

  createController(zoomToFit = true) {
    let renderer: Renderer | null = null;
    let ctrl: Controller;
    const pattern = new Parser(this.props.siteswap).parse();
    renderer = new Canvas2DRenderer(
      this.#canvasRef.current as HTMLCanvasElement,
      [20, 20, 120, 20]
    );

    ctrl = new Controller(pattern, renderer, this.props.cycleDuration);
    ctrl.setTimeScale(this.props.timeScale);
    ctrl.setPaused(this.props.paused);
    ctrl.setCadence(this.props.dwellTimeF, this.props.beatShiftF);
    if (zoomToFit) ctrl.zoomToFit();
    this.#ctrl = ctrl;
  }

  destroyController() {
    if (this.#ctrl) {
      this.#ctrl.destroy();
      this.#ctrl = null;
    }
  }

  componentWillUnmount() {
    this.destroyController();
  }

  componentDidUpdate(prevProps: EngineProps) {
    if (!this.#ctrl) {
      return;
    }
    const recreate =
      prevProps.siteswap !== this.props.siteswap ||
      prevProps.cycleDuration !== this.props.cycleDuration;
    if (recreate) {
      this.destroyController();
      this.createController(false);
    }
    if (recreate || prevProps.timeScale !== this.props.timeScale) {
      this.#ctrl.setTimeScale(this.props.timeScale);
    }
    if (recreate || prevProps.paused !== this.props.paused) {
      this.#ctrl.setPaused(this.props.paused);
    }
    if (recreate || prevProps.handSequence !== this.props.handSequence) {
      this.#ctrl.setHandSequence(this.props.handSequence);
      this.#ctrl.zoomToFit();
    }
    if (
      recreate ||
      prevProps.dwellTimeF !== this.props.dwellTimeF ||
      prevProps.beatShiftF !== this.props.beatShiftF
    ) {
      this.#ctrl.setCadence(this.props.dwellTimeF, this.props.beatShiftF);
    }
  }

  render() {
    return <canvas ref={this.#canvasRef} />;
  }
}

function formatMs(ms: number) {
  return `${(ms / 1000).toFixed(2)}s`;
}

function App() {
  const stdSiteswap = "3";
  const stdPattern = new Parser(stdSiteswap).parse();
  const stdLimits = getCadenceLimits(stdPattern);
  const CustomHandSequenceLabel = "(Custom)";

  const [state, setState] = useState({
    advanced: false,
    library: false,
    generator: false,
    timeScale: 0.4,
    paused: false,
    cycleDuration: 300,
    // Dwell ration 2/3 is normal.
    dwellTimeF: 0.6,
    beatShiftF: 0.5,
    handSequence: parseHandSequence(HandSequenceMap[DefaultHandSequenceName]),
    siteswap: stdSiteswap,
    limits: stdLimits,
  });
  const [form, setForm] = useState({
    handSequenceName: DefaultHandSequenceName,
    siteswap: state.siteswap,
    handSequenceNotation: HandSequenceMap[DefaultHandSequenceName],
    handSequenceValid: true,
    siteswapValid: true,
  });

  function pickPattern(p: Pattern) {
    let seqName = DefaultHandSequenceName;
    let notation = HandSequenceMap[seqName];
    if (p.hands) {
      notation = p.hands;
      seqName = CustomHandSequenceLabel;
    }
    setForm((form) => {
      return {
        ...form,
        handSequenceName: seqName,
        handSequenceNotation: notation,
        handSequenceValid: true,
        siteswap: p.siteswap,
        siteswapValid: true,
      };
    });
    updateSiteswap(p.siteswap, notation);
  }

  function togglePause(e: React.MouseEvent<HTMLButtonElement>) {
    setState((state) => {
      return { ...state, paused: !state.paused };
    });
  }

  function toggleAdvanced() {
    setState((state) => {
      return {
        ...state,
        library: false,
        generator: false,
        advanced: !state.advanced,
      };
    });
  }

  function toggleLibrary() {
    setState((state) => {
      return {
        ...state,
        advanced: false,
        generator: false,
        library: !state.library,
      };
    });
  }

  function toggleGenerator() {
    setState((state) => {
      return {
        ...state,
        advanced: false,
        library: false,
        generator: !state.generator,
      };
    });
  }

  function selectHandSequence(seqName: string) {
    if (seqName === CustomHandSequenceLabel) {
      setForm({
        ...form,
        handSequenceName: seqName,
      });
    } else {
      const notation = HandSequenceMap[seqName];
      setForm((form) => {
        return {
          ...form,
          handSequenceValid: true,
          handSequenceName: seqName,
          handSequenceNotation: HandSequenceMap[seqName],
        };
      });
      setState((state) => {
        return { ...state, handSequence: parseHandSequence(notation) };
      });
    }
  }

  function changeHandSequence(notation: string) {
    let valid = true;
    try {
      const seq = parseHandSequence(notation);
      setState((state) => {
        return { ...state, handSequence: seq };
      });
    } catch (e) {
      valid = false;
    }
    setForm((form) => {
      return {
        ...form,
        handSequenceNotation: notation,
        handSequenceValid: valid,
      };
    });
  }

  function updateSiteswap(siteswap?: string, notation?: string) {
    siteswap = siteswap || form.siteswap;
    try {
      const pattern = isPlayableSiteswap(siteswap);
      if (!pattern) {
        throw new Error();
      }
      const limits = getCadenceLimits(pattern);
      const state2 = {
        ...state,
        paused: false,
        dwellTimeF: Math.min(
          Math.max(state.dwellTimeF, limits.dwellF[0]),
          limits.dwellF[1]
        ),
        beatShiftF: Math.min(
          Math.max(state.beatShiftF, limits.shiftF[0]),
          limits.shiftF[1]
        ),
        limits: limits,
        siteswap: siteswap,
      };
      if (notation) {
        state2.handSequence = parseHandSequence(notation);
      }
      setState(state2);
    } catch (e) {
      setForm((form) => {
        return { ...form, siteswapValid: false };
      });
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.panels}>
        <div className={styles.scene}>
          <Engine
            handSequence={state.handSequence}
            cycleDuration={state.cycleDuration}
            dwellTimeF={state.dwellTimeF}
            beatShiftF={state.beatShiftF}
            timeScale={state.timeScale}
            siteswap={state.siteswap}
            paused={state.paused}
          />
          <div className={styles.tools}>
            <div className={styles.slomo}>
              <span aria-label="Slow" role="img">
                🐌
              </span>
              <RangeInput
                title={(state.timeScale * 100).toFixed(0) + "%"}
                type="range"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setState({ ...state, timeScale: e.target.valueAsNumber })
                }
                value={state.timeScale}
                min={0.1}
                max={1.5}
                step={0.05}
              />
              <span aria-label="Fast" role="img">
                🐇
              </span>
            </div>
            <div className={styles.toolbar}>
              <div className={styles.siteswap}>
                <PlayButton onClick={togglePause} paused={state.paused} />
                <input
                  className={form.siteswapValid ? "" : styles.invalid}
                  type="text"
                  size={1}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") updateSiteswap();
                  }}
                  onBlur={(e) => updateSiteswap()}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      siteswapValid: true,
                      siteswap: e.target.value,
                    })
                  }
                  value={form.siteswap}
                />
              </div>
              <button
                onClick={toggleLibrary}
                className={[
                  styles.library,
                  state.library ? styles.active : "",
                ].join(" ")}
              >
                <span role="img" aria-label="Library">
                  📖
                </span>
              </button>
              <button
                onClick={toggleGenerator}
                className={[
                  styles.generator,
                  state.generator ? styles.active : "",
                ].join(" ")}
              >
                <span role="img" aria-label="Generator">
                  🎲
                </span>
              </button>
              <button
                className={[
                  styles.advanced,
                  state.advanced ? styles["active"] : "",
                ].join(" ")}
                onClick={toggleAdvanced}
              >
                <span role="img" aria-label="Advanced">
                  ⚙
                </span>
              </button>
            </div>
          </div>
        </div>
        <div
          className={[
            styles.panel,
            styles["panel-library"],
            state.library ? styles["panel-open"] : styles["panel-closed"],
          ].join(" ")}
        >
          <Library onPatternChange={(p) => pickPattern(p)} />
        </div>
        <div
          className={[
            styles.panel,
            styles["panel-generator"],
            state.generator ? styles["panel-open"] : styles["panel-closed"],
          ].join(" ")}
        >
          <Generator
            open={state.generator}
            onSiteswapChange={(ss) => updateSiteswap(ss)}
          />
        </div>
        <div
          className={[
            styles.panel,
            styles["panel-advanced"],
            state.advanced ? styles["panel-open"] : styles["panel-closed"],
          ].join(" ")}
        >
          <div className={styles.advanced}>
            <label>Hand Movement</label>
            <select
              value={form.handSequenceName}
              size={HandSequenceChoices.length}
              onChange={(e) => selectHandSequence(e.target.value)}
            >
              {[[CustomHandSequenceLabel, ""]]
                .concat(HandSequenceChoices)
                .map((o) => (
                  <option key={o[0]} value={o[0]}>
                    {o[0]}
                  </option>
                ))}
            </select>
            <label>Hand Movement</label>
            <input
              type="text"
              size={1}
              className={form.handSequenceValid ? "" : styles.invalid}
              disabled={form.handSequenceName !== CustomHandSequenceLabel}
              onChange={(e) => changeHandSequence(e.target.value)}
              value={form.handSequenceNotation}
            />
            <label>
              <span>Dwell</span>
              <span>
                {formatMs(state.dwellTimeF * state.cycleDuration)} /
                {state.dwellTimeF.toFixed(1)} ratio
              </span>
            </label>
            <input
              type="range"
              onChange={(e) =>
                setState({ ...state, dwellTimeF: e.target.valueAsNumber })
              }
              value={state.dwellTimeF}
              min={state.limits.dwellF[0]}
              max={state.limits.dwellF[1]}
              step={0.1}
            />
            <label>
              <span> Cycle Time</span>
              <span>
                {formatMs(state.cycleDuration)} /
                {((2 * 1000) / state.cycleDuration).toFixed(1)}BPS
              </span>
            </label>
            <input
              type="range"
              onChange={(e) =>
                setState({ ...state, cycleDuration: e.target.valueAsNumber })
              }
              value={state.cycleDuration}
              min={200}
              max={750}
              step={10}
            />
            <label>
              <span> Beat Shift</span>
              <span>
                {(
                  state.beatShiftF * state.cycleDuration -
                  0.5 * state.cycleDuration
                ).toFixed(0)}
                ms
              </span>
            </label>
            <input
              disabled={state.limits.shiftF[0] === state.limits.shiftF[1]}
              type="range"
              onChange={(e) =>
                setState({ ...state, beatShiftF: e.target.valueAsNumber })
              }
              value={state.beatShiftF}
              min={state.limits.shiftF[0]}
              max={state.limits.shiftF[1]}
              step={0.1}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
